import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Container, Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import HistoricoPadraoService from '~/Services/HistoricoPadraoService';

const ITENS_POR_PAGINA = 25;

const COLUNAS = [
  {
    title: 'Código',
    key: 'codigo_historico',
    ordenacao: true,
    width: '120px',
  },
  {
    title: 'Descrição',
    key: 'descricao_historico',
    ordenacao: true,
  },
];

const normalizarResponse = (res) => {
  let items = [];

  if (Array.isArray(res)) {
    items = res;
  } else if (res && Array.isArray(res.itens || res.items)) {
    // BE devolve "itens" (PT, padrão snake_case do projeto); aceita "items" por robustez
    items = res.itens || res.items;
  }

  return {
    items: items.map(item => ({
      ...item,
      id: item.codigo_historico,
    })),
    total: res && res.total != null ? res.total : items.length,
  };
};

const ordenarLocalmente = (dados, ordenacao) => {
  if (!ordenacao.coluna) return dados;

  return [...dados].sort((a, b) => {
    const valorA = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
    const valorB = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';

    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
    }

    const textoA = String(valorA).toLowerCase();
    const textoB = String(valorB).toLowerCase();

    if (textoA < textoB) return ordenacao.direcao === 'asc' ? -1 : 1;
    if (textoA > textoB) return ordenacao.direcao === 'asc' ? 1 : -1;
    return 0;
  });
};

const RenderizarPaginacao = ({ paginaAtual, totalPaginas, mudarPagina }) => {
  if (totalPaginas <= 1) return null;

  const itens = [];
  const maxBotoes = 5;

  let inicioPaginas = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  let fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);

  if (fimPaginas - inicioPaginas < maxBotoes - 1) {
    inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
  }

  itens.push(
    <Pagination.First
      key="first"
      onClick={() => mudarPagina(1)}
      disabled={paginaAtual === 1}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      onClick={() => mudarPagina(paginaAtual - 1)}
      disabled={paginaAtual === 1}
    />
  );

  if (inicioPaginas > 1) {
    itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
  }

  for (let numero = inicioPaginas; numero <= fimPaginas; numero += 1) {
    itens.push(
      <Pagination.Item
        key={numero}
        active={numero === paginaAtual}
        onClick={() => mudarPagina(numero)}
      >
        {numero}
      </Pagination.Item>
    );
  }

  if (fimPaginas < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
  }

  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => mudarPagina(paginaAtual + 1)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => mudarPagina(totalPaginas)}
      disabled={paginaAtual === totalPaginas}
    />
  );

  return (
    <div className="d-flex justify-content-end align-items-center mb-2">
      <Pagination className="mb-0">{itens}</Pagination>
    </div>
  );
};

const HistoricoPadrao = () => {
  const [dados, setDados] = useState([]);
  const [total, setTotal] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [ordenacao, setOrdenacao] = useState({
    coluna: 'codigo_historico',
    direcao: 'asc',
  });
  const [loading, setLoading] = useState(false);
  const [carregou, setCarregou] = useState(false);

  const buscar = async () => {
    setLoading(true);
    try {
      const res = await HistoricoPadraoService.listar({
        page: paginaAtual,
        pageSize: ITENS_POR_PAGINA,
        sort: ordenacao.coluna,
        direction: ordenacao.direcao,
      });
      const normalizado = normalizarResponse(res);
      setDados(normalizado.items);
      setTotal(normalizado.total);
      setCarregou(true);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao carregar Histórico Padrão.';
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscar();
  }, [paginaAtual, ordenacao.coluna, ordenacao.direcao]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOrdenacaoChange = (novaOrdenacao) => {
    setOrdenacao(novaOrdenacao);
    setPaginaAtual(1);
  };

  const dadosOrdenados = useMemo(
    () => ordenarLocalmente(dados, ordenacao),
    [dados, ordenacao]
  );

  const totalPaginas = Math.max(1, Math.ceil(total / ITENS_POR_PAGINA));
  const indiceInicial =
    total === 0 ? 0 : (paginaAtual - 1) * ITENS_POR_PAGINA + 1;
  const indiceFinal = Math.min(paginaAtual * ITENS_POR_PAGINA, total);

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Histórico Padrão" />

      <Card className="mb-2">
        <Card.Body>
          {loading && <Loading />}

          {!loading && carregou && total === 0 && (
            <Alert variant="info" className="py-2 mb-0">
              Nenhum histórico padrão encontrado.
            </Alert>
          )}

          {!loading && total > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <small className="text-muted">
                  Exibindo {indiceInicial} a {indiceFinal} de {total} registros
                </small>
                <RenderizarPaginacao
                  paginaAtual={paginaAtual}
                  totalPaginas={totalPaginas}
                  mudarPagina={setPaginaAtual}
                />
              </div>

              <Tabela
                colunas={COLUNAS}
                dados={dadosOrdenados}
                ordenacao={ordenacao}
                onOrdenacaoChange={handleOrdenacaoChange}
                striped
                hover
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default HistoricoPadrao;
